// Reads a local video File's duration before it is uploaded.
//
// The point is cost, not validation theatre: MediaConvert bills per minute of OUTPUT, and a clip's
// duration isn't known server-side until the transcode has already run and been paid for. Catching
// an over-long file here means the admin gets a clear message instead of a silently truncated clip.
// MediaConvert's InputClipping is the actual guarantee (see MAX_CLIP_DURATION_SEC) — this is the
// friendly half of the pair.
//
// It degrades to "don't know" rather than throwing. A probe that fails must never block an
// otherwise-valid upload: the server-side clipping still bounds the cost, so the worst case of a
// failed probe is a clip that gets trimmed without a warning first.
//
// FRAME RATE IS DELIBERATELY NOT MEASURED. The browser exposes no frame-rate metadata; the only
// route is sampling requestVideoFrameCallback during playback, which needs the page to be VISIBLE
// and compositing — it returns nothing in a backgrounded tab, and Firefox has no rVFC at all. It
// also cost ~2s of dead time on every file pick. Measured against what it saves: a >60 fps source
// costs 1.5x rather than 1.25x per minute, which at the largest plan's upload allowance is about
// $1.50/month. Not worth the latency or a check that fails silently — the frame-rate multiplier is
// accepted, and duration (the unbounded risk) is what gets guarded.

export interface VideoProbe {
  /** Seconds. `null` when the browser could not determine it (broken or exotic container). */
  durationSec: number | null;
}

// Metadata usually resolves in tens of milliseconds; a file the decoder can't parse resolves never,
// so the wait is capped to stop a bad file wedging the upload dialog open.
const METADATA_TIMEOUT_MS = 5000;

export async function probeVideo(file: File): Promise<VideoProbe> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  // 'metadata' is all we need — it stops the browser buffering the whole file, which for a 500 MB
  // clip would otherwise read the lot just to answer one question.
  video.preload = 'metadata';
  video.muted = true;
  video.src = url;

  try {
    return { durationSec: await readDuration(video) };
  } catch {
    return { durationSec: null };
  } finally {
    // Order matters: drop the source before revoking, or Safari can keep the blob alive and leak
    // the whole file (which here is up to 500 MB).
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  }
}

function readDuration(video: HTMLVideoElement): Promise<number | null> {
  return new Promise((resolve) => {
    const finish = (v: number | null) => {
      clearTimeout(timer);
      video.removeEventListener('loadedmetadata', onMeta);
      video.removeEventListener('error', onErr);
      resolve(v);
    };
    const onMeta = () => {
      const d = video.duration;
      // A live/fragmented MP4 reports Infinity, and an undecodable one reports NaN. Neither is a
      // duration we can act on, so both become "unknown" rather than a bogus number.
      finish(Number.isFinite(d) && d > 0 ? d : null);
    };
    const onErr = () => finish(null);
    const timer = setTimeout(() => finish(null), METADATA_TIMEOUT_MS);
    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('error', onErr);
  });
}
